import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Loader2,
  LogOut,
  Mail,
  Save,
  User,
} from "lucide-react";
import {
  extractTeacherRecord,
  getTeacherDisplayName,
  getTeacherEmail,
  teacherApi,
} from "../../features/teacher/teacherApi";
import {
  clearTeacherSession,
  updateTeacherProfile,
} from "../../features/teacher/teacherSlice";
import {
  normalizeAudPriceForDisplay,
  normalizeAudPriceToMinorUnits,
} from "../../utils/audCurrency";

const YEAR_OPTIONS = [
  { id: 6, label: "Year 1" },
  { id: 7, label: "Year 2" },
  { id: 8, label: "Year 3" },
  { id: 9, label: "Year 4" },
  { id: 10, label: "Year 5" },
  { id: 11, label: "Year 6" },
  { id: 12, label: "Year 7" },
  { id: 13, label: "Year 8" },
  { id: 1, label: "Year 9" },
  { id: 2, label: "Year 10" },
  { id: 3, label: "Year 11" },
  { id: 4, label: "Year 12" },
  { id: 5, label: "Year 13" },
];

const SPECIALIZATION_OPTIONS = [
  "Visual learning",
  "Exam preparation",
  "Problem solving",
  "Mental maths",
  "Foundational maths",
  "Advanced concepts",
];

const updateProfileStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap');

  .teacher-update-page {
    min-height: 100vh;
    background:
      radial-gradient(circle at top left, rgba(255, 194, 142, 0.28), transparent 26%),
      linear-gradient(180deg, #fcf7ef 0%, #f7f1e7 100%);
    color: #1f1a17;
    font-family: "Lato", sans-serif;
  }

  .teacher-update-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.95);
    border-bottom: 1px solid #ebe4d8;
    backdrop-filter: blur(14px);
  }

  .teacher-update-shell {
    width: min(100%, 78rem);
    margin: 0 auto;
    padding-inline: clamp(0.875rem, 2vw, 1.25rem);
  }

  .teacher-update-topbar-row {
    min-height: clamp(4rem, 5.5vw, 4.5rem);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .teacher-update-brand {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .teacher-update-brand-badge {
    width: clamp(2.25rem, 2.8vw, 2.45rem);
    height: clamp(2.25rem, 2.8vw, 2.45rem);
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    color: #ffffff;
    box-shadow: 0 12px 20px rgba(255, 98, 77, 0.2);
  }

  .teacher-update-brand-title {
    margin: 0;
    color: #ff624d;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    font-weight: 700;
  }

  .teacher-update-actions {
    display: flex;
    align-items: center;
    gap: 0.9rem;
  }

  .teacher-update-email-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    min-height: 2.5rem;
    padding-inline: 1rem;
    border: 1px solid #dbe3f2;
    border-radius: 999px;
    background: #ffffff;
    color: #6f6a67;
    font-size: 0.9rem;
    white-space: nowrap;
  }

  .teacher-update-logout {
    display: inline-flex;
    align-items: center;
    gap: 0.625rem;
    border: 0;
    background: transparent;
    color: #1f1a17;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
  }

  .teacher-update-content {
    padding: clamp(1.25rem, 2.5vw, 1.75rem) clamp(0.875rem, 1.6vw, 1.25rem)
      clamp(2rem, 4vw, 2.75rem);
  }

  .teacher-update-banner {
    border: 1px solid #dbe5fb;
    border-radius: 1.25rem;
    background: #dfe8fb;
    padding: 1.25rem 1.35rem;
    margin-bottom: 1.25rem;
  }

  .teacher-update-banner h1 {
    margin: 0 0 0.45rem;
    font-size: 1.2rem;
    font-weight: 800;
  }

  .teacher-update-banner p {
    margin: 0;
    color: #6a6c77;
  }

  .teacher-update-status {
    border-radius: 1rem;
    border: 1px solid transparent;
    padding: 0.95rem 1rem;
    margin-bottom: 1rem;
    font-size: 0.94rem;
  }

  .teacher-update-status-error {
    border-color: #ffd1c7;
    background: #fff3f0;
    color: #b34736;
  }

  .teacher-update-status-success {
    border-color: #d7efd7;
    background: #effbf0;
    color: #2f7c35;
  }

  .teacher-update-card {
    border: 1px solid #e6e0d6;
    border-radius: 1.25rem;
    background: rgba(255, 255, 255, 0.95);
    padding: clamp(1rem, 2vw, 1.6rem);
    box-shadow: 0 24px 40px rgba(111, 92, 52, 0.08);
  }

  .teacher-update-card-title {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    margin-bottom: 1.25rem;
    font-size: 1.1rem;
    font-weight: 800;
  }

  .teacher-update-photo-row {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    margin-bottom: 1.5rem;
  }

  .teacher-update-photo-box {
    width: 6rem;
    height: 6rem;
    border-radius: 1rem;
    overflow: hidden;
    background: #101010;
    display: grid;
    place-items: center;
    color: #ffffff;
    font-size: 1.35rem;
    font-weight: 800;
    flex: 0 0 auto;
    box-shadow: 0 16px 26px rgba(0, 0, 0, 0.16);
  }

  .teacher-update-photo-box img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .teacher-update-photo-meta {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.7rem;
  }

  .teacher-update-upload-note {
    color: #7a7673;
    font-size: 0.9rem;
  }

  .teacher-update-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .teacher-update-field {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  .teacher-update-field-full {
    grid-column: 1 / -1;
  }

  .teacher-update-field label {
    font-size: 0.96rem;
    font-weight: 700;
  }

  .teacher-update-input,
  .teacher-update-textarea {
    width: 100%;
    min-height: 3.1rem;
    border: 1px solid #e2e7ee;
    border-radius: 0.85rem;
    background: #ffffff;
    color: #1f1a17;
    font: inherit;
    font-size: 0.95rem;
    padding: 0.85rem 1rem;
  }

  .teacher-update-textarea {
    min-height: 7rem;
    resize: vertical;
  }

  .teacher-update-chip-section {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
  }

  .teacher-update-chip-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .teacher-update-chip-grid-specializations {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .teacher-update-chip {
    min-height: 3rem;
    border: 1px solid #e1e5ec;
    border-radius: 0.8rem;
    background: #ffffff;
    color: #2a2522;
    font: inherit;
    font-size: 0.94rem;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
  }

  .teacher-update-chip-active {
    border-color: #ff6f59;
    background: #fff1ec;
    color: #c94d3d;
    font-weight: 700;
  }

  .teacher-update-footer {
    margin-top: 1rem;
  }

  .teacher-update-save {
    width: 100%;
    min-height: 3.35rem;
    border: 0;
    border-radius: 0.9rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: linear-gradient(135deg, #ff7154 0%, #ff564d 100%);
    box-shadow: 0 18px 28px rgba(255, 98, 77, 0.22);
    color: #ffffff;
    font: inherit;
    font-size: 1rem;
    font-weight: 800;
    cursor: pointer;
  }

  .teacher-update-save:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  @media (max-width: 900px) {
    .teacher-update-chip-grid,
    .teacher-update-chip-grid-specializations {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  @media (max-width: 768px) {
    .teacher-update-topbar-row {
      min-height: auto;
      padding: 1rem 0;
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-update-actions {
      width: 100%;
      flex-direction: column;
      align-items: stretch;
    }

    .teacher-update-email-pill,
    .teacher-update-logout {
      justify-content: center;
    }

    .teacher-update-photo-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .teacher-update-grid {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 540px) {
    .teacher-update-chip-grid,
    .teacher-update-chip-grid-specializations {
      grid-template-columns: 1fr;
    }
  }
`;

const toTrimmedString = (value) =>
  value === null || typeof value === "undefined" ? "" : String(value).trim();

const pickFirstValue = (...values) =>
  values.map((value) => toTrimmedString(value)).find(Boolean) || "";

const parseList = (value) => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => parseList(item));
  }

  if (value && typeof value === "object") {
    return Object.values(value).flatMap((item) => parseList(item));
  }

  const normalized = toTrimmedString(value);
  if (!normalized) return [];

  return normalized
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const getPhotoUrl = (teacher = {}) =>
  pickFirstValue(
    teacher?.photoImage,
    teacher?.photo,
    teacher?.profile_photo,
    teacher?.profilePhoto,
    teacher?.image,
    teacher?.avatar
  );

const getInitials = (value = "") =>
  String(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "TP";

const buildUpdatePayload = (formState, initialFormState) => {
  const payload = {};

  if (formState.name !== initialFormState.name) {
    payload.name = formState.name.trim();
  }

  if (formState.phone !== initialFormState.phone) {
    payload.phone = formState.phone.trim();
  }

  if (formState.photoImage !== initialFormState.photoImage) {
    payload.photoImage = formState.photoImage.trim();
  }

  if (formState.certificateImage !== initialFormState.certificateImage) {
    payload.certificateImage = formState.certificateImage.trim();
  }

  if (formState.qualifications !== initialFormState.qualifications) {
    payload.qualifications = formState.qualifications.trim();
  }

  if (formState.specializations.join(", ") !== initialFormState.specializations.join(", ")) {
    payload.specializations = formState.specializations.join(", ");
  }

  if (formState.subjects !== initialFormState.subjects) {
    payload.subjects = formState.subjects.trim();
  }

  if (formState.pricePerSession !== initialFormState.pricePerSession) {
    payload.price_per_session =
      normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0;
  }

  if (formState.experience !== initialFormState.experience) {
    payload.experience = Number(formState.experience || 0);
  }

  if (JSON.stringify(formState.yearsYouTeach) !== JSON.stringify(initialFormState.yearsYouTeach)) {
    payload.year = formState.yearsYouTeach;
  }

  return payload;
};

const createFormState = (teacher = {}) => ({
  name: pickFirstValue(teacher.name, teacher.full_name),
  phone: pickFirstValue(teacher.phone),
  photoImage: pickFirstValue(teacher.photoImage),
  certificateImage: pickFirstValue(teacher.certificateImage),
  qualifications: pickFirstValue(teacher.qualifications, teacher.qualification),
  experience: pickFirstValue(teacher.experience),
  subjects: pickFirstValue(teacher.subjects),
  pricePerSession:
    normalizeAudPriceForDisplay(pickFirstValue(teacher.price_per_session)) ?? "",
  yearsYouTeach: parseList(teacher.year).map((value) => Number(value)).filter(Number.isFinite),
  specializations: parseList(
    teacher.specializations ||
      teacher.teaching_specializations ||
      teacher.specialization
  ),
});

export default function TeacherUpdateProfile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const teacherSession = useSelector((state) => state.teacher?.teacher);
  const initialFormRef = useRef(createFormState(teacherSession || {}));
  const [formState, setFormState] = useState(() => createFormState(teacherSession || {}));
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(() => getPhotoUrl(teacherSession || {}));
  const [statusMessage, setStatusMessage] = useState("");

  const profileQuery = useQuery({
    queryKey: ["teacher-profile-update"],
    queryFn: teacherApi.getProfile,
    retry: false,
  });

  useEffect(() => {
    const teacherRecord = extractTeacherRecord(profileQuery.data);

    if (!teacherRecord) return;

    dispatch(updateTeacherProfile(teacherRecord));
    const nextFormState = createFormState(teacherRecord);
    initialFormRef.current = nextFormState;
    setFormState(nextFormState);
    setPhotoPreviewUrl(getPhotoUrl(teacherRecord));
  }, [dispatch, profileQuery.data]);

  const teacherProfile = useMemo(
    () => extractTeacherRecord(profileQuery.data, teacherSession || {}) || teacherSession || {},
    [profileQuery.data, teacherSession]
  );

  const teacherEmail = getTeacherEmail(teacherProfile) || "Teacher account";
  const teacherName = getTeacherDisplayName(teacherProfile);

  const updateProfileMutation = useMutation({
    mutationFn: (payload) => teacherApi.updateProfile(payload),
    onSuccess: (response) => {
      const teacherRecord = extractTeacherRecord(response, {
        ...teacherProfile,
        name: formState.name,
        phone: formState.phone,
        photoImage: formState.photoImage,
        certificateImage: formState.certificateImage,
        qualifications: formState.qualifications,
        experience: formState.experience,
        subjects: formState.subjects,
        price_per_session:
          normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0,
        year: formState.yearsYouTeach,
        specializations: formState.specializations,
      }) || {
        ...teacherProfile,
        name: formState.name,
        phone: formState.phone,
        photoImage: formState.photoImage,
        certificateImage: formState.certificateImage,
        qualifications: formState.qualifications,
        experience: formState.experience,
        subjects: formState.subjects,
        price_per_session:
          normalizeAudPriceToMinorUnits(formState.pricePerSession) || 0,
        year: formState.yearsYouTeach,
        specializations: formState.specializations,
      };

      dispatch(updateTeacherProfile(teacherRecord));
      navigate("/teacherprofile", { replace: true });
    },
    onError: (error) => {
      setStatusMessage(error.message || "Unable to update the teacher profile.");
    },
  });

  const handleLogout = () => {
    dispatch(clearTeacherSession());
    navigate("/teacherlogin", { replace: true });
  };

  const toggleChip = (key, value) => {
    setFormState((previous) => {
      const exists = previous[key].includes(value);
      return {
        ...previous,
        [key]: exists
          ? previous[key].filter((item) => item !== value)
          : [...previous[key], value],
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatusMessage("");
    const payload = buildUpdatePayload(formState, initialFormRef.current);

    if (!Object.keys(payload).length) {
      setStatusMessage("No profile fields have changed.");
      return;
    }

    updateProfileMutation.mutate(payload);
  };

  return (
    <>
      <style>{updateProfileStyles}</style>
      <main className="teacher-update-page">
        <header className="teacher-update-topbar">
          <div className="teacher-update-shell">
            <div className="teacher-update-topbar-row">
              <div className="teacher-update-brand">
                <div className="teacher-update-brand-badge" aria-hidden="true">
                  <BookOpen size={20} />
                </div>
                <h1 className="teacher-update-brand-title">Teacher Dashboard</h1>
              </div>

              <div className="teacher-update-actions">
                <div className="teacher-update-email-pill">
                  <Mail size={18} />
                  {teacherEmail}
                </div>
                <button
                  className="teacher-update-logout"
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="teacher-update-shell teacher-update-content">
          <section className="teacher-update-banner">
            <h1>Update teacher profile</h1>
            <p>Complete your profile to start teaching</p>
          </section>

          {profileQuery.isLoading ? (
            <div className="teacher-update-status teacher-update-status-success">
              Loading teacher profile...
            </div>
          ) : null}

          {profileQuery.isError ? (
            <div className="teacher-update-status teacher-update-status-error">
              {profileQuery.error.message}
            </div>
          ) : null}

          {statusMessage ? (
            <div className="teacher-update-status teacher-update-status-error">
              {statusMessage}
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <section className="teacher-update-card">
              <div className="teacher-update-card-title">
                <User size={18} />
                Update teacher profile
              </div>

              <div className="teacher-update-photo-row">
                <div className="teacher-update-photo-box" aria-hidden="true">
                  {photoPreviewUrl ? (
                    <img src={photoPreviewUrl} alt={`${teacherName} profile`} />
                  ) : (
                    getInitials(formState.name || teacherName)
                  )}
                </div>

                <div className="teacher-update-photo-meta">
                  <span className="teacher-update-upload-note">
                    Use the image URL/path fields below. This API expects strings for
                    `photoImage` and `certificateImage`.
                  </span>
                </div>
              </div>

              <div className="teacher-update-grid">
                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-name">Full Name</label>
                  <input
                    id="teacher-update-name"
                    className="teacher-update-input"
                    value={formState.name}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        name: event.target.value,
                      }))
                    }
                    placeholder="John Doe"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-phone">Phone</label>
                  <input
                    id="teacher-update-phone"
                    className="teacher-update-input"
                    value={formState.phone}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        phone: event.target.value,
                      }))
                    }
                    placeholder="9876543210"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-photo">Photo Image URL/Path</label>
                  <input
                    id="teacher-update-photo"
                    className="teacher-update-input"
                    value={formState.photoImage}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setFormState((previous) => ({
                        ...previous,
                        photoImage: nextValue,
                      }));
                      setPhotoPreviewUrl(nextValue.trim());
                    }}
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-certificate">
                    Certificate Image URL/Path
                  </label>
                  <input
                    id="teacher-update-certificate"
                    className="teacher-update-input"
                    value={formState.certificateImage}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        certificateImage: event.target.value,
                      }))
                    }
                    placeholder="https://example.com/certificate.jpg"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-qualification">Qualification</label>
                  <input
                    id="teacher-update-qualification"
                    className="teacher-update-input"
                    value={formState.qualifications}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        qualifications: event.target.value,
                      }))
                    }
                    placeholder="MSc Mathematics"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-experience">Years of Experience</label>
                  <input
                    id="teacher-update-experience"
                    className="teacher-update-input"
                    type="number"
                    value={formState.experience}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        experience: event.target.value,
                      }))
                    }
                    placeholder="5"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-subjects">Subjects</label>
                  <textarea
                    id="teacher-update-subjects"
                    className="teacher-update-textarea"
                    value={formState.subjects}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        subjects: event.target.value,
                      }))
                    }
                    placeholder="Maths"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label htmlFor="teacher-update-price">Price Per Session (AUD)</label>
                  <input
                    id="teacher-update-price"
                    className="teacher-update-input"
                    type="number"
                    step="0.01"
                    min="0.50"
                    value={formState.pricePerSession}
                    onChange={(event) =>
                      setFormState((previous) => ({
                        ...previous,
                        pricePerSession: event.target.value,
                      }))
                    }
                    placeholder="17.00"
                  />
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label>Years You Teach</label>
                  <div className="teacher-update-chip-section">
                    <div className="teacher-update-chip-grid">
                      {YEAR_OPTIONS.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          className={`teacher-update-chip ${
                            formState.yearsYouTeach.includes(option.id)
                              ? "teacher-update-chip-active"
                              : ""
                          }`}
                          onClick={() => toggleChip("yearsYouTeach", option.id)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="teacher-update-field teacher-update-field-full">
                  <label>Teaching Specializations</label>
                  <div className="teacher-update-chip-section">
                    <div className="teacher-update-chip-grid teacher-update-chip-grid-specializations">
                      {SPECIALIZATION_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`teacher-update-chip ${
                            formState.specializations.includes(option)
                              ? "teacher-update-chip-active"
                              : ""
                          }`}
                          onClick={() => toggleChip("specializations", option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="teacher-update-footer">
              <button
                className="teacher-update-save"
                type="submit"
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                   Update Profile
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </>
  );
}
